import { useState, type ReactNode } from 'react'
import type { components } from '../../../api/generated/schema'
import {
  Banner,
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  SectionLabel,
  Textarea,
} from '../../../design/components'
import {
  disciplinesOf,
  specializationDiscipline,
  specializationElement,
  type Discipline,
} from '../types'
import type { CharacterEditing } from '../useCharacterEditing'
import { SHEET_SECTION_ID } from './sheetSections'
import styles from './sections.module.css'

type Schema = components['schemas']
type Element = Schema['SpecializationElement']

/**
 * The spellcasting cards: specializations, focus items, enchanted items and rote
 * spells. All collapsed, all further down the scroll (spec §5.1).
 *
 * Every budget here — available / used / remaining, and the spells' derived
 * shifts and stress — is computed server-side. Focus and enchanted items share
 * one magic-item pool, which is why both cards show the same budget and both
 * carry the same over-spend warning. None of the edits are optimistic for
 * exactly that reason: they all move a budget this client is not allowed to
 * compute.
 *
 * The two item forms preview what an item will cost, from the same rules the
 * server applies (two slots per point of focus Control/Power; for an enchanted
 * item one slot for itself, one per shift of power above Lore, and one per two
 * charges above the first). Those are previews only — the cost that gets stored
 * is always the server's, so a stale client cannot talk the pool into a number
 * it did not agree to.
 */

const ELEMENTS: Element[] = [
  'None',
  'Fire',
  'Water',
  'Earth',
  'Air',
  'Spirit',
  'Divination',
  'Conjuration',
  'Transformation',
  'Crafting',
  'Fields',
  'Death',
]

/** Both disciplines, in the order their columns are read. */
const DISCIPLINES: Discipline[] = ['Evocation', 'Thaumaturgy']

/** What a discipline the server sent no budget for is worth: nothing, spelled out. */
const EMPTY_BUDGET: Schema['SpecializationBudgetDto'] = {
  available: 0,
  used: 0,
  remaining: 0,
  isOverLimit: false,
}

/**
 * Which elements belong to which discipline.
 *
 * A specialization is always bought inside a discipline's column now, so the element picker
 * can be narrowed to that discipline instead of offering all eleven. It has to be: the server
 * files the chosen element under the column matching the discipline
 * (`EvocationElement` or `ThaumaturgyElement`), so an Evocation specialization in Divination
 * would be stored as an Evocation element that no rote spell's element ever matches.
 */
const DISCIPLINE_ELEMENTS: Record<Discipline, Element[]> = {
  Evocation: ['Fire', 'Water', 'Earth', 'Air', 'Spirit'],
  Thaumaturgy: ['Divination', 'Conjuration', 'Transformation', 'Crafting', 'Fields', 'Death'],
}

export function SpecializationsCard({
  specializations,
  spellcasting = 'Both',
  editing,
}: {
  specializations: Schema['SpecializationsSectionDto'] | undefined
  /**
   * Which disciplines the character's abilities grant. Both columns are drawn either way;
   * this only decides which of them says no ability has opened it yet.
   */
  spellcasting?: Schema['SpellcastingType'] | undefined
  editing?: CharacterEditing | undefined
}) {
  // Which column's form is open, if any. One at a time — the two columns share the sheet's
  // width, and two open forms would leave neither readable.
  const [adding, setAdding] = useState<Discipline | null>(null)
  const entries = specializations?.specializations ?? []
  const canEdit = editing?.canEdit === true
  const granted = disciplinesOf(spellcasting)

  if (specializations === undefined) return null
  if (entries.length === 0 && !canEdit) return null

  return (
    <CollapsibleCard
      id={SHEET_SECTION_ID.specializations}
      empty={entries.length === 0}
      variant="magic"
      label="Specializations"
      summary={String(entries.length)}
    >
      {/* Both columns, always and at every width — a specialization is read one discipline at
          a time, and a column that disappears when its budget is empty moves the other one
          under the reader's thumb between visits. A discipline no ability grants says so in
          place. */}
      <div className={styles.disciplines}>
        {DISCIPLINES.map((discipline) => (
          <DisciplineColumn
            key={discipline}
            discipline={discipline}
            granted={granted.includes(discipline)}
            budget={
              discipline === 'Evocation' ? specializations.evocation : specializations.thaumaturgy
            }
            entries={entries.filter((entry) => specializationDiscipline(entry) === discipline)}
            adding={adding === discipline}
            onAdd={() => setAdding(discipline)}
            {...(canEdit && editing !== undefined ? { editing } : {})}
          />
        ))}
      </div>

      {/* Below the columns rather than inside the one that opened it: a column is half a phone
          wide, which is no place for a select, two number fields and two buttons. The form's
          title carries the discipline, so nothing is lost by moving it out. */}
      {canEdit && editing !== undefined && adding !== null && (
        <SpecializationForm discipline={adding} editing={editing} onClose={() => setAdding(null)} />
      )}
    </CollapsibleCard>
  )
}

/**
 * One discipline's half of the card: its budget, its specializations, and its own add button.
 *
 * The add button is what makes the discipline dropdown unnecessary — the column the reader
 * pressed *is* the answer, so the form is handed a fixed discipline rather than asking for one
 * it already knows.
 */
function DisciplineColumn({
  discipline,
  granted,
  budget,
  entries,
  adding,
  onAdd,
  editing,
}: {
  discipline: Discipline
  /** Whether an ability actually grants this discipline. Ungranted still shows — with a note. */
  granted: boolean
  budget: Schema['SpecializationBudgetDto'] | undefined
  entries: Schema['SpecializationDto'][]
  /** Whether this column's form is the one currently open, which retires its add button. */
  adding: boolean
  onAdd: () => void
  editing?: CharacterEditing
}) {
  const usable = budget ?? EMPTY_BUDGET

  return (
    <section className={styles.discipline} aria-label={discipline}>
      {/* The name gets its own line so it never has to share half a column with a 44px
          button — "Thaumaturgy" set in micro caps is wider than what would be left. */}
      <SectionLabel tone="muted" micro>
        {discipline}
      </SectionLabel>
      <div className={styles.disciplineHead}>
        <span className={usable.isOverLimit === true ? styles.metaValueWarn : styles.metaValue}>
          {usable.used ?? 0} / {usable.available ?? 0}
        </span>
        {editing !== undefined && !adding && (
          <IconButton
            label={`Add ${discipline} specialization`}
            icon="add"
            onClick={onAdd}
            disabled={editing.addSpecialization.isPending}
          />
        )}
      </div>

      {!granted && <p className={styles.empty}>No ability grants {discipline}.</p>}
      {granted && entries.length === 0 && <p className={styles.empty}>None yet.</p>}

      {entries.map((entry) => (
        <div key={entry.id} className={styles.specRow}>
          {/* Name and cost alone on the first line. Two columns on a phone leave about 145px
              a piece, and "Transformation" only fits when nothing else is beside it. */}
          <div className={styles.specHead}>
            <span className={styles.rowName}>{specializationElement(entry)}</span>
            <span className={styles.rowBadge}>{entry.cost ?? 0}</span>
          </div>
          {/* Plain text, not the `.tag` pills the wider cards use: a pill's padding and
              letter-spacing make "Control +1" 92px, and two of those in a 145px column put
              every bonus on a line of its own. Set plain they share one line with the
              remove glyph, which is three lines a row saved. */}
          <div className={styles.specStats}>
            <span>
              Control <b className={styles.specStatValue}>+{entry.control ?? 0}</b>
            </span>
            <span>
              Power <b className={styles.specStatValue}>+{entry.power ?? 0}</b>
            </span>
            {editing !== undefined && (
              <IconButton
                label={`Remove ${specializationElement(entry)} specialization`}
                icon="delete"
                className={styles.rowRemove}
                disabled={editing.removeSpecialization.isPending}
                onClick={() =>
                  editing.removeSpecialization.mutate({ specializationId: entry.id ?? 0 })
                }
              />
            )}
          </div>
        </div>
      ))}
    </section>
  )
}

export function FocusItemsCard({
  focusItems,
  editing,
}: {
  focusItems: Schema['FocusItemsSectionDto'] | undefined
  editing?: CharacterEditing | undefined
}) {
  const [adding, setAdding] = useState(false)
  const items = focusItems?.items ?? []
  const canEdit = editing?.canEdit === true

  if (focusItems === undefined) return null
  if (items.length === 0 && !canEdit) return null

  return (
    <CollapsibleCard
      id={SHEET_SECTION_ID.focusItems}
      empty={items.length === 0}
      variant="magic"
      label="Focus items"
      summary={String(items.length)}
      action={
        canEdit && editing !== undefined && !adding ? (
          <IconButton label="Add focus item" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      <div className={styles.budget}>
        <Budget label="Magic items" budget={focusItems.budget} />
      </div>

      <SharedPoolWarning budget={focusItems.budget} />

      {items.length === 0 && <p className={styles.empty}>None yet.</p>}

      {items.map((item) => (
        <div key={item.id} className={styles.row}>
          <div className={styles.rowHead}>
            <span className={styles.rowName}>{item.name}</span>
            <span className={styles.rowBadge}>{item.cost ?? 0}</span>
          </div>
          {item.description !== undefined && item.description !== '' && (
            <p className={styles.rowText}>{item.description}</p>
          )}
          <div className={styles.rowTags}>
            <span className={styles.tag}>{String(item.element ?? '')}</span>
            {(item.controlBonus ?? 0) > 0 && (
              <span className={styles.tag}>Control +{item.controlBonus}</span>
            )}
            {(item.powerBonus ?? 0) > 0 && (
              <span className={styles.tag}>Power +{item.powerBonus}</span>
            )}
            <span className={styles.tag}>{slots(item.cost ?? 0)}</span>
          </div>
          {canEdit && editing !== undefined && (
            <RemoveRow
              pending={editing.removeFocusItem.isPending}
              onRemove={() => editing.removeFocusItem.mutate({ itemId: item.id ?? 0 })}
            />
          )}
        </div>
      ))}

      {canEdit && editing !== undefined && adding && (
        <FocusItemForm editing={editing} onClose={() => setAdding(false)} />
      )}
    </CollapsibleCard>
  )
}

export function EnchantedItemsCard({
  enchantedItems,
  editing,
}: {
  enchantedItems: Schema['EnchantedItemsSectionDto'] | undefined
  editing?: CharacterEditing | undefined
}) {
  const [adding, setAdding] = useState(false)
  const items = enchantedItems?.items ?? []
  const canEdit = editing?.canEdit === true
  const anySpent = items.some((item) => (item.currentUses ?? 0) < (item.maxUses ?? 0))

  if (enchantedItems === undefined) return null
  if (items.length === 0 && !canEdit) return null

  return (
    <CollapsibleCard
      id={SHEET_SECTION_ID.enchantedItems}
      empty={items.length === 0}
      variant="magic"
      label="Enchanted items"
      summary={String(items.length)}
      action={
        canEdit && editing !== undefined ? (
          <>
            {/* Charges are per session, so the reset that actually happens at the table is
                the whole shelf at once. It sits in the header for that reason. */}
            {items.length > 0 && (
              <IconButton
                label="Refill all items"
                icon="refresh"
                disabled={editing.refreshAllEnchantedItems.isPending || !anySpent}
                onClick={() => editing.refreshAllEnchantedItems.mutate()}
              />
            )}
            {!adding && (
              <IconButton label="Add enchanted item" icon="add" onClick={() => setAdding(true)} />
            )}
          </>
        ) : undefined
      }
    >
      {/* The same pool the focus-item card spends from — shown on both so neither card
          looks like it has a budget of its own. */}
      <div className={styles.budget}>
        <Budget label="Magic items" budget={enchantedItems.budget} />
      </div>

      <SharedPoolWarning budget={enchantedItems.budget} />

      {items.length === 0 && <p className={styles.empty}>None yet.</p>}

      {items.map((item) => (
        <div key={item.id} className={styles.row}>
          <div className={styles.rowHead}>
            <span className={styles.rowName}>{item.name}</span>
            <span className={styles.rowBadge}>
              {item.currentUses ?? 0}/{item.maxUses ?? 0} uses
            </span>
          </div>
          {item.description !== undefined && item.description !== '' && (
            <p className={styles.rowText}>{item.description}</p>
          )}
          <div className={styles.rowTags}>
            <span className={item.exceedsPowerCap === true ? styles.tagWarn : styles.tag}>
              Power {item.power ?? 0}
            </span>
            {item.weapon !== undefined && item.weapon !== '' && (
              <span className={styles.tag}>Weapon {item.weapon}</span>
            )}
            {item.armor !== undefined && item.armor !== '' && (
              <span className={styles.tag}>Armor {item.armor}</span>
            )}
            <span className={styles.tag}>{slots(item.cost ?? 0)}</span>
          </div>
          {item.exceedsPowerCap === true && (
            <p className={styles.rowText}>
              Over the {enchantedItems.maxPower ?? 0}-shift ceiling for a Lore of{' '}
              {enchantedItems.basePower ?? 0}.
            </p>
          )}
          {canEdit && editing !== undefined && (
            <div className={styles.rowActions}>
              <Button
                variant="secondary"
                disabled={editing.useEnchantedItem.isPending || (item.currentUses ?? 0) <= 0}
                onClick={() => editing.useEnchantedItem.mutate({ itemId: item.id ?? 0 })}
              >
                Use one
              </Button>
              <Button
                variant="secondary"
                disabled={
                  editing.refreshEnchantedItem.isPending ||
                  (item.currentUses ?? 0) >= (item.maxUses ?? 0)
                }
                onClick={() => editing.refreshEnchantedItem.mutate({ itemId: item.id ?? 0 })}
              >
                Refill
              </Button>
              <Button
                variant="danger"
                disabled={editing.removeEnchantedItem.isPending}
                onClick={() => editing.removeEnchantedItem.mutate({ itemId: item.id ?? 0 })}
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      ))}

      {canEdit && editing !== undefined && adding && (
        <EnchantedItemForm
          basePower={enchantedItems.basePower ?? 0}
          maxPower={enchantedItems.maxPower ?? 0}
          usesPerSlot={enchantedItems.usesPerSlot ?? 2}
          baseUses={enchantedItems.baseUses ?? 1}
          baseCost={enchantedItems.baseCost ?? 1}
          editing={editing}
          onClose={() => setAdding(false)}
        />
      )}
    </CollapsibleCard>
  )
}

export function RoteSpellsCard({
  roteSpells,
  editing,
}: {
  roteSpells: Schema['RoteSpellsSectionDto'] | undefined
  editing?: CharacterEditing | undefined
}) {
  const [adding, setAdding] = useState(false)
  const spells = roteSpells?.spells ?? []
  const canEdit = editing?.canEdit === true

  if (roteSpells === undefined) return null
  if (spells.length === 0 && !canEdit) return null

  return (
    <CollapsibleCard
      id={SHEET_SECTION_ID.roteSpells}
      empty={spells.length === 0}
      variant="magic"
      label="Rote spells"
      summary={`${roteSpells.used ?? 0}/${roteSpells.limit ?? 0}`}
      action={
        canEdit && editing !== undefined && !adding ? (
          <IconButton label="Add rote spell" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {spells.length === 0 && <p className={styles.empty}>None yet.</p>}

      {spells.map((spell) => (
        <div key={spell.id} className={styles.row}>
          <div className={styles.rowHead}>
            <span className={styles.rowName}>{spell.name}</span>
            <span className={styles.rowBadge}>
              {spell.shifts ?? 0}/{spell.maxShifts ?? 0} shifts
            </span>
          </div>
          {spell.description !== undefined && spell.description !== '' && (
            <p className={styles.rowText}>{spell.description}</p>
          )}
          <div className={styles.rowTags}>
            <span className={styles.tag}>{String(spell.element ?? '')}</span>
            <span className={styles.tag}>{spell.stress ?? 0} stress</span>
          </div>
          {canEdit && editing !== undefined && (
            <RemoveRow
              pending={editing.removeRoteSpell.isPending}
              onRemove={() => editing.removeRoteSpell.mutate({ spellId: spell.id ?? 0 })}
            />
          )}
        </div>
      ))}

      {canEdit && editing !== undefined && adding && (
        <RoteSpellForm editing={editing} onClose={() => setAdding(false)} />
      )}
    </CollapsibleCard>
  )
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

/**
 * The add form for one discipline.
 *
 * There is no discipline field: the column the reader pressed already chose it, and re-asking
 * would let them submit an Evocation specialization from under the Thaumaturgy heading. The
 * element list is that discipline's, for the same reason.
 */
function SpecializationForm({
  discipline,
  editing,
  onClose,
}: {
  discipline: Discipline
  editing: CharacterEditing
  onClose: () => void
}) {
  const elements = DISCIPLINE_ELEMENTS[discipline]
  const [element, setElement] = useState<Element>(elements[0] ?? 'Fire')
  const [control, setControl] = useState('1')
  const [power, setPower] = useState('0')

  return (
    <FormShell
      title={`New ${discipline} specialization`}
      pending={editing.addSpecialization.isPending}
      disabled={anyNegative(control, power)}
      onCancel={onClose}
      onSubmit={() =>
        editing.addSpecialization.mutate(
          {
            spellcastingType: discipline,
            element,
            control: Number(control) || 0,
            power: Number(power) || 0,
          },
          { onSuccess: onClose },
        )
      }
    >
      <Select label="Element" value={element} onChange={setElement} options={elements} />
      <NumberField label="Control" value={control} onChange={setControl} />
      <NumberField label="Power" value={power} onChange={setPower} />
    </FormShell>
  )
}

function FocusItemForm({ editing, onClose }: { editing: CharacterEditing; onClose: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [element, setElement] = useState<Element>('Fire')
  const [controlBonus, setControl] = useState('1')
  const [powerBonus, setPower] = useState('0')

  // Mirrors the server's rule so the price is visible before the item is bought. The
  // server still derives the stored cost — this only previews it.
  const cost = 2 * (Math.max(0, Number(controlBonus) || 0) + Math.max(0, Number(powerBonus) || 0))

  return (
    <FormShell
      title="New focus item"
      pending={editing.addFocusItem.isPending}
      disabled={name.trim() === '' || anyNegative(controlBonus, powerBonus)}
      onCancel={onClose}
      onSubmit={() =>
        editing.addFocusItem.mutate(
          {
            name,
            description: description === '' ? null : description,
            element,
            controlBonus: Number(controlBonus) || 0,
            powerBonus: Number(powerBonus) || 0,
          },
          { onSuccess: onClose },
        )
      }
    >
      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Select label="Element" value={element} onChange={setElement} options={ELEMENTS} />
      <NumberField label="Control bonus" value={controlBonus} onChange={setControl} />
      <NumberField label="Power bonus" value={powerBonus} onChange={setPower} />
      <CostPreview cost={cost} hint="Two slots per point of Control or Power." />
    </FormShell>
  )
}

function EnchantedItemForm({
  basePower,
  maxPower,
  baseUses,
  usesPerSlot,
  baseCost,
  editing,
  onClose,
}: {
  /** Free power a new item starts with: the character's Lore rating. */
  basePower: number
  /** Twice Lore — going past it is allowed, and warned about. */
  maxPower: number
  /** Uses per session before any slot is spent on charges. */
  baseUses: number
  /** Extra uses one further slot buys. */
  usesPerSlot: number
  /** Slots the item itself costs, before a single upgrade is bought. */
  baseCost: number
  editing: CharacterEditing
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [weapon, setWeapon] = useState('')
  const [armor, setArmor] = useState('')
  const [maxUses, setMaxUses] = useState(String(baseUses))
  const [power, setPower] = useState(String(basePower))

  // The same two previews as the focus form, and for the same reason: the numbers below
  // are what the server will derive from these fields, not a second implementation of the
  // rule. Uses are snapped up because a partial slot buys nothing.
  const requestedUses = Math.max(0, Number(maxUses) || 0)
  const chargeSlots = Math.ceil(Math.max(0, requestedUses - baseUses) / usesPerSlot)
  const grantedUses = baseUses + chargeSlots * usesPerSlot
  const requestedPower = Math.max(0, Number(power) || 0)
  const powerSlots = Math.max(0, requestedPower - basePower)
  const effectivePower = basePower + powerSlots
  const overCap = effectivePower > maxPower

  return (
    <FormShell
      title="New enchanted item"
      pending={editing.addEnchantedItem.isPending}
      disabled={name.trim() === '' || anyNegative(power, maxUses)}
      onCancel={onClose}
      onSubmit={() =>
        editing.addEnchantedItem.mutate(
          {
            name,
            description: description === '' ? null : description,
            weapon: weapon === '' ? null : weapon,
            armor: armor === '' ? null : armor,
            maxUses: requestedUses,
            power: requestedPower,
          },
          { onSuccess: onClose },
        )
      }
    >
      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Input label="Weapon rating" value={weapon} onChange={(e) => setWeapon(e.target.value)} />
      <Input label="Armor rating" value={armor} onChange={(e) => setArmor(e.target.value)} />
      <NumberField
        label="Power"
        value={power}
        onChange={setPower}
        hint={`Free up to your Lore of ${basePower}; one slot per shift above it.`}
      />
      <NumberField
        label="Uses per session"
        value={maxUses}
        onChange={setMaxUses}
        step={usesPerSlot}
        hint={`${baseUses} free, then +${usesPerSlot} per slot.`}
      />
      <CostPreview
        cost={baseCost + powerSlots + chargeSlots}
        hint={`Power ${effectivePower}, ${grantedUses} use${grantedUses === 1 ? '' : 's'} per session.`}
      />
      {overCap && (
        <Banner tone="warn">
          Power {effectivePower} is past the {maxPower}-shift ceiling for a Lore of {basePower}. It
          will still be saved.
        </Banner>
      )}
    </FormShell>
  )
}

function RoteSpellForm({ editing, onClose }: { editing: CharacterEditing; onClose: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [element, setElement] = useState<Element>('Fire')
  const [shifts, setShifts] = useState('3')

  return (
    <FormShell
      title="New rote spell"
      pending={editing.addRoteSpell.isPending}
      disabled={name.trim() === '' || anyNegative(shifts)}
      onCancel={onClose}
      onSubmit={() =>
        editing.addRoteSpell.mutate(
          {
            name,
            description: description === '' ? null : description,
            element,
            shifts: Number(shifts) || 0,
          },
          { onSuccess: onClose },
        )
      }
    >
      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Select label="Element" value={element} onChange={setElement} options={ELEMENTS} />
      <NumberField
        label="Shifts"
        value={shifts}
        onChange={setShifts}
        hint="The server caps this against Discipline, specializations and focus items."
      />
    </FormShell>
  )
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

function FormShell({
  title,
  pending,
  disabled = false,
  onCancel,
  onSubmit,
  children,
}: {
  title: string
  pending: boolean
  disabled?: boolean
  onCancel: () => void
  onSubmit: () => void
  children: ReactNode
}) {
  return (
    <form
      className={styles.inlineForm}
      style={{ marginTop: 'var(--sp-4)' }}
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <SectionLabel tone="ember" micro>
        {title}
      </SectionLabel>
      {children}
      <div className={styles.actions}>
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || disabled}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}

function Select<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: T
  onChange: (next: T) => void
  options: readonly T[]
}) {
  return (
    <label>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      <select
        className={styles.levelSelect}
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  )
}

function RemoveRow({ pending, onRemove }: { pending: boolean; onRemove: () => void }) {
  return (
    <div className={styles.rowActions}>
      <Button variant="danger" disabled={pending} onClick={onRemove}>
        Remove
      </Button>
    </div>
  )
}

function Budget({
  label,
  budget,
}: {
  label: string
  budget: Schema['MagicBudgetDto'] | Schema['SpecializationBudgetDto'] | undefined
}) {
  if (budget === undefined) return null

  const overLimit = 'isOverLimit' in budget && budget.isOverLimit === true

  return (
    <div className={styles.metaItem}>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      <span className={overLimit ? styles.metaValueWarn : styles.metaValue}>
        {budget.used ?? 0} / {budget.available ?? 0}
      </span>
    </div>
  )
}

/**
 * The over-spend warning, on both magic-item cards.
 *
 * It is a warning and not a block on purpose: the server saves an over-budget item and
 * reports it, matching every other budget on the sheet. The table decides what is legal.
 */
function SharedPoolWarning({ budget }: { budget: Schema['MagicBudgetDto'] | undefined }) {
  if (budget?.isOverLimit !== true) return null

  return (
    <div className={styles.pyramidWarning}>
      <Banner tone="warn">
        Focus and enchanted items share one pool of {budget.available ?? 0} slots, and{' '}
        {budget.used ?? 0} are spent. Drop an item or take another spellcasting ability.
      </Banner>
    </div>
  )
}

/** What an item about to be added will cost, in the same words the rows use. */
function CostPreview({ cost, hint }: { cost: number; hint: string }) {
  return (
    <div className={styles.metaItem}>
      <SectionLabel tone="muted" micro>
        Cost
      </SectionLabel>
      <span className={styles.metaValue}>{slots(cost)}</span>
      <span className={styles.rowText}>{hint}</span>
    </div>
  )
}

/** "1 slot" / "4 slots" — the pool's unit, spelled so a bare number is never ambiguous. */
function slots(cost: number): string {
  return `${cost} slot${cost === 1 ? '' : 's'}`
}

/**
 * A whole-number field that says so, in place, when it has gone negative.
 *
 * Every number on these forms is a count - of points, shifts or charges - and the server
 * answers a negative one with a 400. That answer used to surface in the sheet's notice
 * area, which on a long sheet is nowhere near the field that caused it. Catching it here
 * puts the message under the input, and the form's submit stays disabled until it is
 * fixed, so the round trip never happens.
 */
function NumberField({
  label,
  value,
  onChange,
  hint,
  step,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  hint?: string
  step?: number
}) {
  const invalid = isNegative(value)

  return (
    <Input
      label={label}
      type="number"
      min={0}
      {...(step === undefined ? {} : { step })}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      invalid={invalid}
      hint={invalid ? `${label} cannot be negative.` : hint}
    />
  )
}

/** True once the reader has typed an actual negative - an empty or half-typed field is not. */
function isNegative(value: string): boolean {
  const parsed = Number(value)
  return value.trim() !== '' && Number.isFinite(parsed) && parsed < 0
}

function anyNegative(...values: string[]): boolean {
  return values.some(isNegative)
}
